import {
  ADDONS,
  ADDON_PRODUCTS,
  EA_HOSTING_PLAN,
  PRODUCTS,
  isDedicatedProduct,
  isEaHosting,
  isSignalManager,
  isSignalManagerLine,
  lineItemProduct,
  subscriptionDisplayName,
  subscriptionIncludesProduct,
  subscriptionLineItems,
  summarizeHeldPlan
} from 'lib/billing';
import type { LibSubscription } from 'lib/billing/pbAdapter';
import { formatEndsOn } from 'sections/account-settings/subscriptions/checkout-v2/Configure/formatEndsOn';
import { CONSEQUENCES, PLAN_GROUPS } from './content';

/** What cancelling touches, read from the subscriptions it will cancel. */
export type CancellationScope = {
  seats: number;
  dedicatedEnvs: number;
  rooms: number;
  hasAnalyzer: boolean;
  hasAiTrading: boolean;
  holdsEaHosting: boolean;
};

export type Consequence = keyof typeof CONSEQUENCES;

export function cancellationScope(subscriptions: LibSubscription[], catalog: object | null): CancellationScope {
  // The billing page's own summary, so both screens read the same numbers.
  const held = summarizeHeldPlan(subscriptions, catalog);
  const { standard = 0, premium = 0, futures = 0 } = held.copierCounts;
  return {
    seats: standard + premium + futures,
    dedicatedEnvs: held.deEnvCount,
    // One subscription per room.
    rooms: subscriptions.filter(isSignalManager).length,
    hasAnalyzer: held.hasAnalyzer,
    hasAiTrading: subscriptionIncludesProduct(subscriptions, ADDON_PRODUCTS.mcp, catalog),
    holdsEaHosting: subscriptions.some(isEaHosting)
  };
}

/** What switches off at period end: only what this customer holds, in the order the dialog lists it. */
export function cancellationConsequences(scope: CancellationScope): Consequence[] {
  const held: Record<Consequence, boolean> = {
    accounts: scope.seats > 0,
    dedicatedEnv: scope.dedicatedEnvs > 0,
    rooms: scope.rooms > 0,
    eaHosting: scope.holdsEaHosting,
    aiConnect: scope.hasAiTrading,
    analyzer: scope.hasAnalyzer
  };
  return (Object.keys(CONSEQUENCES) as Consequence[]).filter((consequence) => held[consequence]);
}

const SEAT_PRODUCTS = new Set<string>([PRODUCTS.standard, PRODUCTS.premium, PRODUCTS.futures]);

type PlanGroupKey = 'copier' | 'dedicated' | 'analyzer' | 'rooms' | 'eaHosting' | 'mcp';

/** One kind of thing being cancelled, gathered across every subscription that holds it. */
export type PlanGroup = {
  key: string;
  name: string;
  /** The line under the name: copier tiers, or how many environments or rooms. */
  detail: string | null;
  /** One entry per dedicated environment, listed under the detail. */
  items: string[];
  periodEnds: number[];
};

// Copier first, as the billing page lists a plan; anything unrecognised follows, named by billing.
const GROUP_ORDER: PlanGroupKey[] = ['copier', 'dedicated', 'analyzer', 'rooms', 'eaHosting', 'mcp'];

const GROUP_NAMES: Record<PlanGroupKey, string> = {
  copier: PLAN_GROUPS.copier,
  dedicated: ADDONS.dedicated.label,
  analyzer: ADDONS.analyzer.label,
  rooms: PLAN_GROUPS.rooms,
  eaHosting: EA_HOSTING_PLAN.label,
  mcp: ADDONS.mcp.label
};

// A futures environment bills flat at quantity 1, which is not an account count.
const environmentLabel = (name: string, product: string | null, quantity: number) =>
  [name || ADDONS.dedicated.label, product !== ADDON_PRODUCTS.dedicatedFutures && quantity > 0 && PLAN_GROUPS.accounts(quantity)]
    .filter(Boolean)
    .join(PLAN_GROUPS.separator);

const lineGroup = (product: string | null, environment: string): PlanGroupKey | null => {
  if (product && SEAT_PRODUCTS.has(product)) return 'copier';
  if (environment || isDedicatedProduct(product)) return 'dedicated';
  if (product === ADDON_PRODUCTS.analyzer) return 'analyzer';
  if (product === ADDON_PRODUCTS.mcp) return 'mcp';
  return null;
};

/**
 * What is being cancelled, by product rather than by subscription: a bundle's copier tiers read as one
 * Copier row, every dedicated environment sits in one block, and each add-on gets its own row.
 */
export function planGroups(subscriptions: LibSubscription[], catalog: object | null): PlanGroup[] {
  const groups = new Map<string, PlanGroup>();
  const add = (key: string, name: string, periodEnd: number, item?: string) => {
    const group = groups.get(key) ?? { key, name, detail: null, items: [], periodEnds: [] };
    group.periodEnds.push(periodEnd);
    if (item) group.items.push(item);
    groups.set(key, group);
  };

  subscriptions.forEach((sub) => {
    // EA hosting is always its own subscription. A room usually is too; bundled with other plans, every
    // line reads on its own.
    if (isEaHosting(sub)) return add('eaHosting', GROUP_NAMES.eaHosting, sub.currentPeriodEnd);
    const lines = subscriptionLineItems(sub);
    if (isSignalManager(sub) && lines.length <= 1) return add('rooms', GROUP_NAMES.rooms, sub.currentPeriodEnd);
    lines.forEach((line) => {
      if (isSignalManagerLine(line)) return add('rooms', GROUP_NAMES.rooms, sub.currentPeriodEnd);
      const product = lineItemProduct(line, catalog, sub);
      // A legacy single-line subscription carries its environment on the subscription, not the line.
      const environment = ('dedicatedNickName' in line && line.dedicatedNickName) || (lines.length === 1 ? sub.dedicatedNickName : '');
      const key = lineGroup(product, environment);
      if (key === 'dedicated')
        return add(key, GROUP_NAMES.dedicated, sub.currentPeriodEnd, environmentLabel(environment, product, line.quantity));
      if (key) return add(key, GROUP_NAMES[key], sub.currentPeriodEnd);
      const name = line.productName || subscriptionDisplayName(sub);
      return add(name, name, sub.currentPeriodEnd);
    });
  });

  const copier = groups.get('copier');
  // The billing page's own tier labels, so a plan reads the same on both screens.
  if (copier) copier.detail = summarizeHeldPlan(subscriptions, catalog).copierLabels.join(PLAN_GROUPS.separator) || null;
  const dedicated = groups.get('dedicated');
  if (dedicated) {
    if (dedicated.items.length > 1) dedicated.name = PLAN_GROUPS.environmentsTitle;
    dedicated.detail = PLAN_GROUPS.environments(dedicated.items.length);
  }
  const rooms = groups.get('rooms');
  if (rooms) rooms.detail = PLAN_GROUPS.roomCount(rooms.periodEnds.length);

  const rank = (key: string) => {
    const index = GROUP_ORDER.indexOf(key as PlanGroupKey);
    return index < 0 ? GROUP_ORDER.length : index;
  };
  return Array.from(groups.values()).sort((a, b) => rank(a.key) - rank(b.key));
}

const periodEnds = (subscriptions: LibSubscription[]) => subscriptions.map((sub) => sub.currentPeriodEnd).filter((seconds) => seconds > 0);

/** The first day something stops, in seconds; undefined when no period end is known. */
export function nextPeriodEnd(subscriptions: LibSubscription[]): number | undefined {
  const ends = periodEnds(subscriptions);
  return ends.length ? Math.min(...ends) : undefined;
}

// Compared as the dates the customer reads: plans renewing the same day differ by hours.
const oneDay = (ends: number[]) => new Set(ends.map((seconds) => formatEndsOn({ seconds }))).size === 1;

/** The one day everything stops, when every subscription ends on the same date; otherwise undefined. */
export function sharedPeriodEnd(subscriptions: LibSubscription[]): number | undefined {
  const ends = periodEnds(subscriptions);
  return ends.length === subscriptions.length && oneDay(ends) ? Math.min(...ends) : undefined;
}

/** A group's end date as its pill shows it: the day, or "From" its first day when its plans differ. */
export function groupEndLabel(ends: number[]): string | null {
  const known = ends.filter((seconds) => seconds > 0);
  if (!known.length) return null;
  const first = formatEndsOn({ seconds: Math.min(...known) });
  if (!first) return null;
  return oneDay(known) ? first : PLAN_GROUPS.from(first);
}

/** A first name fit to greet with: Auth0 falls back to the email when no name was given. */
export function greetingName(firstName?: string): string {
  const name = (firstName || '').trim().split(/\s+/)[0] || '';
  return name.includes('@') ? '' : name;
}
